import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  Carousel, ProgressBar, Tabs, Tab
} from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const AcademicEventsManager = () => {
  const [events, setEvents] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tempPhotos, setTempPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  
  const [formData, setFormData] = useState({
    academic_year_id: '',
    event_type: '',
    event_name: '',
    number_of_participants: '',
    date_from: '',
    date_to: '',
    report_link: '',
    uploaded_by: 1,
    photos: []
  });

  // API configuration
  const API_BASE_URL = BASE_URL;
  const API_URL = `${API_BASE_URL}/academic_events.php`;

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchAcademicYears();
    fetchEventTypes();
    fetchEvents();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_URL}?academic_years=true`);
      if (response.data.success) {
        setAcademicYears(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching academic years:', err);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}?event_types=true`);
      if (response.data.success) {
        setEventTypes(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching event types:', err);
    }
  };

  const fetchEvents = async (academicYearId = null) => {
    try {
      setLoading(true);
      setError('');
      
      let url = API_URL;
      if (academicYearId && academicYearId !== 'all') {
        url += `?academic_year_id=${academicYearId}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        setEvents(response.data.data);
      } else {
        setError('Failed to load events: ' + response.data.message);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (e, type = 'photo') => {
    const files = type === 'photo' ? Array.from(e.target.files) : [e.target.files[0]];
    if (files.length === 0) return;
    
    try {
      setUploadingFiles(true);
      setUploadProgress(0);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (type === 'photo') {
        files.forEach((file) => {
          formData.append('photos[]', file);
        });
      } else {
        formData.append('file', files[0]);
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/upload_academic_event_files.php`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data.success) {
        if (type === 'photo') {
          const uploadedPhotos = response.data.uploaded_files.map(file => ({
            photo_path: file.photo_path,
            caption: '',
            thumbnail_path: file.thumbnail_path
          }));
          
          setTempPhotos(prev => [...prev, ...uploadedPhotos]);
          setSuccess(`${uploadedPhotos.length} photo(s) uploaded successfully!`);
        } else {
          setFormData(prev => ({
            ...prev,
            report_link: response.data.uploaded_files[0].file_path
          }));
          setSuccess('Report file uploaded successfully!');
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to upload files: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading files: ' + err.message);
    } finally {
      setUploadingFiles(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const handleRemoveTempPhoto = (index) => {
    setTempPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingPhoto = (photoId) => {
    setPhotosToDelete(prev => [...prev, photoId]);
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId)
    }));
  };

  const handleRemoveReport = () => {
    setFormData(prev => ({
      ...prev,
      report_link: ''
    }));
  };

  const handlePhotoDragEnd = (result) => {
    if (!result.destination) return;
    
    const photos = editingEvent 
      ? [...formData.photos] 
      : [...tempPhotos];
    
    const [reorderedItem] = photos.splice(result.source.index, 1);
    photos.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedPhotos = photos.map((photo, index) => ({
      ...photo,
      display_order: index + 1
    }));
    
    if (editingEvent) {
      setFormData(prev => ({
        ...prev,
        photos: updatedPhotos
      }));
    } else {
      setTempPhotos(updatedPhotos);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let dataToSend = { ...formData };
      
      if (editingEvent) {
        // For editing, handle photos updates
        dataToSend.photos_to_add = tempPhotos;
        dataToSend.photos_to_delete = photosToDelete;
        
        // Reordering info
        const photosOrder = formData.photos.map((photo, index) => ({
          id: photo.id,
          display_order: index + 1
        }));
        dataToSend.photos_order = photosOrder;
        
        delete dataToSend.photos; // Remove original photos array
        
        const response = await axios.put(
          `${API_URL}?id=${editingEvent.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Event updated successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      } else {
        // For new event, include temp photos
        dataToSend.photos = tempPhotos;
        
        const response = await axios.post(
          API_URL,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          setSuccess('Academic event added successfully!');
        } else {
          setError('Operation failed: ' + (response.data.message || ''));
          return;
        }
      }
      
      setShowModal(false);
      resetForm();
      setEditingEvent(null);
      fetchEvents(selectedYear === 'all' ? null : selectedYear);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError('Error saving event: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event? All associated photos and reports will also be deleted.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_URL}?id=${id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success) {
          setSuccess('Event deleted successfully!');
          fetchEvents(selectedYear === 'all' ? null : selectedYear);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete event: ' + (response.data.message || ''));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Error deleting event: ' + err.message);
      }
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      academic_year_id: event.academic_year_id,
      event_type: event.event_type,
      event_name: event.event_name,
      number_of_participants: event.number_of_participants || '',
      date_from: event.date_from ? event.date_from.split(' ')[0] : '',
      date_to: event.date_to ? event.date_to.split(' ')[0] : '',
      report_link: event.report_link || '',
      uploaded_by: event.uploaded_by || 1,
      photos: event.photos || []
    });
    setTempPhotos([]);
    setPhotosToDelete([]);
    setShowModal(true);
  };

  const handleYearChange = (yearId) => {
    setSelectedYear(yearId);
    fetchEvents(yearId === 'all' ? null : yearId);
  };

  const resetForm = () => {
    setFormData({
      academic_year_id: '',
      event_type: '',
      event_name: '',
      number_of_participants: '',
      date_from: '',
      date_to: '',
      report_link: '',
      uploaded_by: 1,
      photos: []
    });
    setTempPhotos([]);
    setPhotosToDelete([]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return 'fa-file';
    
    const extension = filePath.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      default:
        return 'fa-file';
    }
  };

  const getFileTypeBadge = (filePath) => {
    if (!filePath) return null;
    
    const extension = filePath.split('.').pop().toLowerCase();
    let bg = 'secondary';
    let text = extension.toUpperCase();
    
    switch (extension) {
      case 'pdf':
        bg = 'danger';
        break;
      case 'doc':
      case 'docx':
        bg = 'primary';
        break;
      case 'xls':
      case 'xlsx':
        bg = 'success';
        break;
      case 'ppt':
      case 'pptx':
        bg = 'warning';
        break;
    }
    
    return <Badge bg={bg} className="me-1">{text}</Badge>;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      'Seminar': 'primary',
      'Workshop': 'success',
      'Webinar': 'info',
      'Technology & Innovation': 'warning',
      'Conferences': 'danger',
      'Skill-Building Activities': 'secondary',
      'Career Development': 'dark',
      'Research & Creative': 'info',
      'Wellness & Personal': 'success',
      'Social & Cultural': 'warning',
      'Academic Support': 'primary',
      'Other': 'secondary'
    };
    return colors[type] || 'secondary';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading academic events...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Academic Events Manager</h2>
            <p className="text-muted">Manage seminars, workshops, conferences and other academic events</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Event
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {/* Filter Section */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Filter by Academic Year</Form.Label>
                  <Form.Select
                    value={selectedYear}
                    onChange={(e) => handleYearChange(e.target.value)}
                  >
                    <option value="all">All Academic Years</option>
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.academic_year}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8} className="text-end">
                <Badge bg="info" className="me-3">
                  Total Events: {events.length}
                </Badge>
                {eventTypes.map(type => {
                  const count = events.filter(e => e.event_type === type).length;
                  if (count > 0) {
                    return (
                      <Badge key={type} bg={getEventTypeColor(type)} className="me-2">
                        {type}: {count}
                      </Badge>
                    );
                  }
                  return null;
                })}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="40">#</th>
                  <th>Event Name</th>
                  <th width="120">Type</th>
                  <th>Academic Year</th>
                  <th width="100">Participants</th>
                  <th width="120">Date</th>
                  <th width="80">Photos</th>
                  <th width="100">Report</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.length > 0 ? (
                  events.map((event, index) => (
                    <tr key={event.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{event.event_name}</strong>
                        <div className="small text-muted">
                          {formatDate(event.date_from)} to {formatDate(event.date_to)}
                        </div>
                      </td>
                      <td>
                        <Badge bg={getEventTypeColor(event.event_type)}>
                          {event.event_type}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg="info">
                          {event.academic_year || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        {event.number_of_participants ? (
                          <Badge bg="primary">
                            {event.number_of_participants}
                          </Badge>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(event.date_from)}
                        </small>
                      </td>
                      <td>
                        <Badge bg={event.photos && event.photos.length > 0 ? 'success' : 'secondary'}>
                          {event.photos ? event.photos.length : 0} photos
                        </Badge>
                      </td>
                      <td>
                        {event.report_link ? (
                          <a 
                            href={getImageUrl(event.report_link)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                            title="View report"
                          >
                            <i className={`fas ${getFileIcon(event.report_link)} text-primary`}></i>
                          </a>
                        ) : (
                          <span className="text-muted small">No report</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(event)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(event.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No events found</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i> Add First Event
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingEvent ? 'Edit Academic Event' : 'Add New Academic Event'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Tabs defaultActiveKey="basic" className="mb-3">
                <Tab eventKey="basic" title="Basic Information">
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Academic Year *</Form.Label>
                        <Form.Select
                          name="academic_year_id"
                          value={formData.academic_year_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Academic Year</option>
                          {academicYears.map((year) => (
                            <option key={year.id} value={year.id}>
                              {year.academic_year}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event Type *</Form.Label>
                        <Form.Select
                          name="event_type"
                          value={formData.event_type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Event Type</option>
                          {eventTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Event Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="event_name"
                      value={formData.event_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Annual Research Seminar, Workshop on AI"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Number of Participants</Form.Label>
                        <Form.Control
                          type="number"
                          name="number_of_participants"
                          value={formData.number_of_participants}
                          onChange={handleInputChange}
                          min="0"
                          placeholder="Optional"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date From *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_from"
                          value={formData.date_from}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date To *</Form.Label>
                        <Form.Control
                          type="date"
                          name="date_to"
                          value={formData.date_to}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>

                <Tab eventKey="photos" title="Event Photos">
                  <div className="mt-3">
                    {/* Upload Progress */}
                    {uploadingFiles && (
                      <div className="mb-3">
                        <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
                        <small className="text-muted d-block mt-1">
                          Uploading files...
                        </small>
                      </div>
                    )}

                    {/* Upload Button */}
                    <Form.Group className="mb-4">
                      <Form.Label>Upload Event Photos</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'photo')}
                        disabled={uploadingFiles}
                        multiple
                      />
                      <Form.Text className="text-muted">
                        Select multiple photos (JPG, PNG, GIF, WebP). Max 5MB each.
                      </Form.Text>
                    </Form.Group>

                    {/* Photos Grid with Drag & Drop */}
                    <h6>Event Photos ({formData.photos.length + tempPhotos.length})</h6>
                    <DragDropContext onDragEnd={handlePhotoDragEnd}>
                      <Droppable droppableId="photos" direction="horizontal">
                        {(provided) => (
                          <div 
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="d-flex flex-wrap gap-3 mb-3"
                          >
                            {/* Existing Photos */}
                            {formData.photos.map((photo, index) => (
                              <Draggable 
                                key={`existing-${photo.id}`} 
                                draggableId={`existing-${photo.id}`} 
                                index={index}
                                isDragDisabled={photosToDelete.includes(photo.id)}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`position-relative ${photosToDelete.includes(photo.id) ? 'opacity-50' : ''}`}
                                    style={{ width: '180px' }}
                                  >
                                    <Card className="border">
                                      <Card.Img
                                        variant="top"
                                        src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                                        alt={`Photo ${index + 1}`}
                                        style={{ height: '120px', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = `https://via.placeholder.com/180x120?text=Photo+${index + 1}`;
                                        }}
                                      />
                                      <Card.Body className="p-2">
                                        <Form.Control
                                          type="text"
                                          placeholder="Caption"
                                          value={photo.caption || ''}
                                          onChange={(e) => {
                                            const newPhotos = [...formData.photos];
                                            newPhotos[index].caption = e.target.value;
                                            setFormData(prev => ({ ...prev, photos: newPhotos }));
                                          }}
                                          size="sm"
                                        />
                                      </Card.Body>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1"
                                        onClick={() => handleRemoveExistingPhoto(photo.id)}
                                        title="Remove photo"
                                      >
                                        <i className="fas fa-times"></i>
                                      </Button>
                                      <div className="position-absolute top-0 start-0 m-1">
                                        <Badge bg="info" style={{ cursor: 'move' }}>
                                          <i className="fas fa-grip-vertical me-1"></i>
                                          {index + 1}
                                        </Badge>
                                      </div>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            ))}

                            {/* New Temporary Photos */}
                            {tempPhotos.map((photo, index) => (
                              <Draggable 
                                key={`temp-${index}`} 
                                draggableId={`temp-${index}`} 
                                index={formData.photos.length + index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{ width: '180px' }}
                                  >
                                    <Card className="border">
                                      <Card.Img
                                        variant="top"
                                        src={getImageUrl(photo.thumbnail_path || photo.photo_path)}
                                        alt={`New photo ${index + 1}`}
                                        style={{ height: '120px', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = `https://via.placeholder.com/180x120?text=New+${index + 1}`;
                                        }}
                                      />
                                      <Card.Body className="p-2">
                                        <Form.Control
                                          type="text"
                                          placeholder="Caption"
                                          value={photo.caption || ''}
                                          onChange={(e) => {
                                            const newPhotos = [...tempPhotos];
                                            newPhotos[index].caption = e.target.value;
                                            setTempPhotos(newPhotos);
                                          }}
                                          size="sm"
                                        />
                                      </Card.Body>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1"
                                        onClick={() => handleRemoveTempPhoto(index)}
                                        title="Remove photo"
                                      >
                                        <i className="fas fa-times"></i>
                                      </Button>
                                      <div className="position-absolute top-0 start-0 m-1">
                                        <Badge bg="warning" style={{ cursor: 'move' }}>
                                          <i className="fas fa-grip-vertical me-1"></i>
                                          {formData.photos.length + index + 1}
                                        </Badge>
                                      </div>
                                    </Card>
                                  </div>
                                )}
                              </Draggable>
                            ))}

                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>

                    {/* No Photos Message */}
                    {(formData.photos.length === 0 && tempPhotos.length === 0) && (
                      <div className="text-center py-4 border rounded bg-light">
                        <i className="fas fa-images fa-2x text-muted mb-3"></i>
                        <p className="text-muted">No photos added yet</p>
                        <small className="d-block text-muted">
                          Upload photos to document this event
                        </small>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="report" title="Event Report">
                  <div className="mt-3">
                    {/* Current Report */}
                    {formData.report_link ? (
                      <div className="mb-4">
                        <Alert variant="info" className="py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <i className={`fas ${getFileIcon(formData.report_link)} me-2`}></i>
                              <span className="me-2">{formData.report_link.split('/').pop()}</span>
                              {getFileTypeBadge(formData.report_link)}
                            </div>
                            <div>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2"
                                href={getImageUrl(formData.report_link)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View report"
                              >
                                <i className="fas fa-eye"></i>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={handleRemoveReport}
                                title="Remove report"
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </div>
                          </div>
                        </Alert>
                      </div>
                    ) : (
                      <Alert variant="light" className="text-center py-4">
                        <i className="fas fa-file-upload fa-2x text-muted mb-3"></i>
                        <p className="text-muted mb-2">No report uploaded</p>
                        <small className="d-block text-muted">
                          Event report is optional
                        </small>
                      </Alert>
                    )}

                    {/* Upload Button */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {formData.report_link ? 'Replace Report File' : 'Upload Report File (Optional)'}
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        onChange={(e) => handleFileUpload(e, 'report')}
                        disabled={uploadingFiles}
                      />
                      <Form.Text className="text-muted">
                        Upload event report (PDF, Word, Excel, PowerPoint, TXT). Max 20MB.
                      </Form.Text>
                    </Form.Group>
                  </div>
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingFiles}>
                {uploadingFiles ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  editingEvent ? 'Update Event' : 'Add Event'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default AcademicEventsManager;